import React, { useCallback, useContext } from 'react';

import { parseMarkdownLocationLink } from '@aline/common';
import { POPOVER_CHILD_CLASS } from 'src/components/reports/report-detail/popover-child-class';

import { Citation } from './Citation';
import { CitationCardDataContext } from './citation-card-data-context';
import { CitationCard } from './CitationCard';
import { useCitationNavigation } from './useCitationNavigation';

interface RelationshipCitationProps {
  href: string;
  referenceText: string;
  /** Carried into a cross-document jump so the destination can highlight this relationship. */
  relationshipId?: string | null;
}

/**
 * Citation component for relationship tooltips.
 * When the citation references the currently open document, scrolls to the location in-place.
 * For PDFs, highlights the referenced text on the correct page.
 * Otherwise, opens the referenced document in a new tab.
 */
export const RelationshipCitation = React.memo(function RelationshipCitation({
  href,
  referenceText,
  relationshipId,
}: RelationshipCitationProps): JSX.Element | null {
  const { getCitationTooltip, navigateToRelationshipCard } = useCitationNavigation();
  const citationCardDataByDocumentId = useContext(CitationCardDataContext);

  const parts = parseMarkdownLocationLink(href);
  const documentId = parts?.documentId;

  const cardData = documentId ? citationCardDataByDocumentId?.get(documentId) : undefined;
  const questionId = cardData?.questionId;

  const goToCitation = useCallback(() => {
    if (!documentId) return;
    navigateToRelationshipCard({
      citation: { href, referenceText },
      documentId,
      questionId,
      relationshipId,
    });
  }, [documentId, href, navigateToRelationshipCard, questionId, referenceText, relationshipId]);

  if (!parts || !documentId) return null;

  const handleClickCitation = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    goToCitation();
  };

  return (
    <Citation
      card={
        cardData && (
          <CitationCard
            caption={cardData.isRowDocument ? 'This document' : null}
            cardClassName={POPOVER_CHILD_CLASS}
            chipLabel={cardData.isRowDocument ? null : cardData.relationshipLabel}
            jumpInPlace={cardData.isRowDocument}
            title={cardData.title}
            onViewInDocument={goToCitation}
          />
        )
      }
      cardClassName={POPOVER_CHILD_CLASS}
      tooltip={getCitationTooltip(href)}
      onClickCitation={handleClickCitation}
    />
  );
});
